import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, inject, Input } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

import { TranslocoPipe } from '@jsverse/transloco';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';

type Value = Array<CheckboxGroupOption> | null;

export interface CheckboxGroupOption {
  label?: string;
  value: string;
  checked: boolean;
}

@Component({
  selector: 'ui-platform-button-checkbox-group',
  templateUrl: './button-checkbox-group.component.html',
  styleUrl: './button-checkbox-group.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    NzCheckboxModule,
    TranslocoPipe
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiPlatformButtonCheckboxGroupComponent),
      multi: true
    }
  ]
})
export class UiPlatformButtonCheckboxGroupComponent implements ControlValueAccessor {
  readonly #changeDetectorRef = inject(ChangeDetectorRef);

  @Input() label = '';
  @Input({ required: true }) checkboxGroupOptions: Array<CheckboxGroupOption> = [];

  disabled = false;
  #internalValue: Value = null;

  syncExternalValue: (value: Value) => void = () => {};
  syncExternalTouched: () => void = () => {};

  onModelChange(value: Value): void {
    const valueToStore =
      value?.map(option => {
        return {
          value: option.value,
          checked: option.checked
        };
      }) ?? null;
    this.syncExternalValue(valueToStore);
    this.syncExternalTouched();
  }

  writeValue(value: Value): void {
    this.#internalValue = value;
    this.#changeDetectorRef.detectChanges();
  }

  registerOnChange(fn: (value: Value) => void): void {
    this.syncExternalValue = (value: Value) => {
      fn(value);
    };
  }

  registerOnTouched(fn: () => void): void {
    this.syncExternalTouched = () => {
      fn();
    };
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    this.#changeDetectorRef.detectChanges();
  }
}
